import React, { useState, useEffect } from 'react';
import {
  FaBuilding, FaDoorOpen, FaUserTie, FaUsers, FaMapMarkerAlt,
  FaEnvelope, FaPhone, FaParking, FaWifi, FaUtensils, FaDesktop,
  FaPlus, FaEdit, FaTrash, FaTimes, FaSearch, FaStar, FaGlobeAmericas
} from 'react-icons/fa';
import useInstructorILTResourceManagementStore from '@/stores/InstructorStore/InstructorILTResourceManagementStore';
import {
  getCountries,
  getStatesByCountry,
  getDistrictsByState,
  Country,
  State,
  District
} from '@/services/PagesServices/ILTResourceManagementService';
import ConfirmationModal from '@/Components/common/ConfirmationModal';
import './ILTResourseManagement.css';

const ILTManagement = () => {
  const {
    locations: storeLocations,
    rooms: storeRooms,
    trainers: storeTrainers,
    stats: storeStats,
    fetchLocations,
    fetchRooms,
    fetchTrainers,
    fetchStats,
    addLocation,
    editLocation,
    removeLocation,
    addRoom,
    editRoom,
    removeRoom,
    addTrainer,
    editTrainer,
    removeTrainer,
    instructors,
    fetchAvailableInstructors,
  } = useInstructorILTResourceManagementStore();

  const [activeTab, setActiveTab] = useState('locations');
  const [showModal, setShowModal] = useState(false);
  const [viewMode, setViewMode] = useState(false);
  const [filterStatus, setFilterStatus] = useState('active');
  const [searchQuery, setSearchQuery] = useState('');

  // Message modal state (for success/error messages)
  const [showMessage, setShowMessage] = useState(false);
  const [messageType, setMessageType] = useState<'success' | 'error'>('success');
  const [messageTitle, setMessageTitle] = useState('');
  const [messageText, setMessageText] = useState('');

  // Edit mode state
  const [editMode, setEditMode] = useState<{
    isEditing: boolean;
    itemId: number | null;
    itemType: 'location' | 'room' | 'trainer' | null;
  }>({ isEditing: false, itemId: null, itemType: null });

  // Confirmation modal state
  const [confirmationModal, setConfirmationModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
  }>({ isOpen: false, title: '', message: '', onConfirm: () => {} });

  // Lazy load data based on active tab
  useEffect(() => {
    // Always fetch stats on mount
    if (!storeStats) {
      fetchStats();
    }
    if (activeTab === 'locations' && !storeLocations) {
      fetchLocations();
    }
    if (activeTab === 'rooms') {
      // Fetch locations for the dropdown in room form
      if (!storeLocations) {
        fetchLocations();
      }
      if (!storeRooms) {
        fetchRooms();
      }
    }
    if (activeTab === 'trainers' && !storeTrainers) {
      fetchTrainers();
    }
  }, [activeTab, storeStats, storeLocations, storeRooms, storeTrainers, fetchStats, fetchLocations, fetchRooms, fetchTrainers]);

  const [locations, setLocations] = useState(storeLocations || []);

  // Sync store data to local state
  useEffect(() => {
    if (storeLocations) {
      setLocations(storeLocations);
    }
  }, [storeLocations]);

  const [rooms, setRooms] = useState(storeRooms || []);

  useEffect(() => {
    if (storeRooms) {
      setRooms(storeRooms);
    }
  }, [storeRooms]);

  const [roomFormData, setRoomFormData] = useState({
    name: '',
    roomNumber: '',
    floor: '',
    location: '',
    type: 'Conference',
    capacity: '',
    area: '',
    description: '',
    amenities: [] as string[]
  });

  const [trainers, setTrainers] = useState(storeTrainers || []);

  useEffect(() => {
    if (storeTrainers) {
      setTrainers(storeTrainers);
    }
  }, [storeTrainers]);

  const [trainerFormData, setTrainerFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    bio: '',
    expertise: [] as string[],
    certifications: [] as string[],
    experience: '',
    rate: '',
    availability: 'Full-Time',
    languages: [] as string[],
    isExternal: false
  });
  // For create mode: the existing instructor (user) this trainer is linked to.
  const [selectedInstructorId, setSelectedInstructorId] = useState('');

  // When an instructor is picked, link by their uuid and mirror their identity
  // fields into the form for visual confirmation (names come from their account).
  const handleInstructorSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const uuid = e.target.value;
    setSelectedInstructorId(uuid);
    const inst = (instructors || []).find(i => i.uuid === uuid);
    if (inst) {
      setTrainerFormData(prev => ({
        ...prev,
        firstName: inst.firstName || '',
        lastName: inst.lastName || '',
        email: inst.email || '',
        phone: inst.phone || ''
      }));
    }
  };

  const [formData, setFormData] = useState({
    name: '', address: '', city: '', state: '', zipCode: '',
    country: 'India', timezone: '', capacity: '', phone: '', email: '',
    facilities: [] as string[],
    countryId: null as number | null,
    stateId: null as number | null,
    districtId: null as number | null
  });

  // Location master data for cascading dropdowns
  const [countries, setCountries] = useState<Country[]>([]);
  const [states, setStates] = useState<State[]>([]);
  const [districts, setDistricts] = useState<District[]>([]);
  const [loadingCountries, setLoadingCountries] = useState(false);
  const [loadingStates, setLoadingStates] = useState(false);
  const [loadingDistricts, setLoadingDistricts] = useState(false);

  // Fetch countries on mount
  useEffect(() => {
    const fetchCountriesData = async () => {
      setLoadingCountries(true);
      const response = await getCountries();
      if (response.success && response.data) {
        setCountries(response.data);
      }
      setLoadingCountries(false);
    };
    fetchCountriesData();
  }, []);

  // Fetch states when country changes
  useEffect(() => {
    const fetchStatesData = async () => {
      if (formData.countryId) {
        setLoadingStates(true);
        setStates([]);
        setDistricts([]);
        const response = await getStatesByCountry(formData.countryId);
        if (response.success && response.data) {
          setStates(response.data);
        }
        setLoadingStates(false);
      } else {
        setStates([]);
        setDistricts([]);
      }
    };
    fetchStatesData();
  }, [formData.countryId]);

  // Fetch districts when state changes
  useEffect(() => {
    const fetchDistrictsData = async () => {
      if (formData.stateId) {
        setLoadingDistricts(true);
        setDistricts([]);
        const response = await getDistrictsByState(formData.stateId);
        if (response.success && response.data) {
          setDistricts(response.data);
        }
        setLoadingDistricts(false);
      } else {
        setDistricts([]);
      }
    };
    fetchDistrictsData();
  }, [formData.stateId]);

  // Handle country change
  const handleCountryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const countryId = e.target.value ? parseInt(e.target.value) : null;
    const country = countries.find(c => c.id === countryId);
    setFormData(prev => ({
      ...prev,
      countryId,
      country: country?.name || '',
      stateId: null,
      state: '',
      districtId: null,
      city: ''
    }));
  };

  // Handle state change
  const handleStateChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const stateId = e.target.value ? parseInt(e.target.value) : null;
    const state = states.find(s => s.id === stateId);
    setFormData(prev => ({
      ...prev,
      stateId,
      state: state?.name || '',
      districtId: null,
      city: ''
    }));
  };

  // Handle district change
  const handleDistrictChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const districtId = e.target.value ? parseInt(e.target.value) : null;
    const district = districts.find(d => d.id === districtId);
    setFormData(prev => ({
      ...prev,
      districtId,
      city: district?.name || ''
    }));
  };

  const stats = storeStats || {
    totalLocations: 0,
    availableRooms: 0,
    activeTrainers: 0,
    totalCapacity: 0
  };

  const facilityOptions = [
    { id: 'parking', label: 'Parking', icon: <FaParking /> },
    { id: 'cafeteria', label: 'Cafeteria', icon: <FaUtensils /> },
    { id: 'wifi', label: 'WiFi', icon: <FaWifi /> },
    { id: 'av', label: 'AV Equipment', icon: <FaDesktop /> },
    { id: 'reception', label: 'Reception' },
    { id: 'catering', label: 'Catering' },
    { id: 'security', label: 'Security' },
    { id: 'accessible', label: 'Accessible' }
  ];

  const amenityOptions = [
    'Projector',
    'Whiteboard',
    'WiFi',
    'Computers',
    'Video Conferencing',
    'Air Conditioning',
    'Catering Available',
    'Parking',
    'Streaming Equipment'
  ];

  const expertiseOptions = [
    'Leadership',
    'Project Management',
    'Agile',
    'Data Science',
    'Sales Training',
    'Communication',
    'Technical Skills',
    'Soft Skills',
    'Change Management',
    'Customer Success',
    'Strategy'
  ];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleRoomInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setRoomFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFacilityToggle = (facility: string) => {
    setFormData(prev => ({
      ...prev,
      facilities: prev.facilities.includes(facility)
        ? prev.facilities.filter(f => f !== facility)
        : [...prev.facilities, facility]
    }));
  };

  const handleAmenityToggle = (amenity: string) => {
    setRoomFormData(prev => ({
      ...prev,
      amenities: prev.amenities.includes(amenity)
        ? prev.amenities.filter(a => a !== amenity)
        : [...prev.amenities, amenity]
    }));
  };

  const handleTrainerInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setTrainerFormData(prev => ({ ...prev, [name]: checked }));
    } else {
      setTrainerFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleExpertiseToggle = (expertise: string) => {
    setTrainerFormData(prev => ({
      ...prev,
      expertise: prev.expertise.includes(expertise)
        ? prev.expertise.filter(e => e !== expertise)
        : [...prev.expertise, expertise]
    }));
  };

  // Helper function to show message modal
  const showMessageModal = (type: 'success' | 'error', title: string, text: string) => {
    setMessageType(type);
    setMessageTitle(title);
    setMessageText(text);
    setShowMessage(true);
  };

  // Helper function to show confirmation modal
  const showConfirmation = (title: string, message: string, onConfirm: () => void) => {
    setConfirmationModal({ isOpen: true, title, message, onConfirm });
  };

  // Edit handlers
  const handleEditLocation = async (location: any) => {
    setEditMode({ isEditing: true, itemId: location.id, itemType: 'location' });
    setFormData({
      name: location.name || '',
      address: location.address || '',
      city: location.city || '',
      state: location.state || '',
      zipCode: location.zipCode || '',
      country: location.country || 'India',
      countryId: location.countryId || null,
      stateId: location.stateId || null,
      districtId: location.districtId || null,
      timezone: location.timezone || '',
      capacity: location.capacity?.toString() || '',
      phone: location.phone || '',
      email: location.email || '',
      facilities: location.facilities || []
    });

    // Load states and districts for editing
    if (location.countryId) {
      const statesResponse = await getStatesByCountry(location.countryId);
      if (statesResponse.success && statesResponse.data) {
        setStates(statesResponse.data);
      }
    }
    if (location.stateId) {
      const districtsResponse = await getDistrictsByState(location.stateId);
      if (districtsResponse.success && districtsResponse.data) {
        setDistricts(districtsResponse.data);
      }
    }

    setShowModal(true);
  };

  const handleEditRoom = (room: any) => {
    setEditMode({ isEditing: true, itemId: room.id, itemType: 'room' });
    setRoomFormData({
      name: room.name,
      roomNumber: room.roomNumber || '',
      floor: room.floor || '',
      location: room.location,
      type: room.type || 'Conference',
      capacity: room.capacity?.toString() || '',
      area: room.area || '',
      description: room.description || '',
      amenities: room.amenities || []
    });
    setShowModal(true);
  };

  const handleEditTrainer = (trainer: any) => {
    setEditMode({ isEditing: true, itemId: trainer.id, itemType: 'trainer' });
    setTrainerFormData({
      firstName: trainer.firstName ?? '',
      lastName: trainer.lastName ?? '',
      email: trainer.email ?? '',
      phone: trainer.phone ?? '',
      bio: trainer.bio ?? '',
      // Guard against missing arrays / numbers from the API. Without this the
      // edit modal threw on `.includes` / `.toString` and never opened.
      expertise: Array.isArray(trainer.expertise) ? trainer.expertise : [],
      certifications: Array.isArray(trainer.certifications) ? trainer.certifications : [],
      experience: trainer.experience != null ? String(trainer.experience) : '',
      rate: trainer.rate != null ? String(trainer.rate) : '',
      availability: trainer.availability ?? 'Full-Time',
      languages: Array.isArray(trainer.languages) ? trainer.languages : [],
      isExternal: !!trainer.isExternal,
    });
    setShowModal(true);
  };

  // View Details / View Profile — reuses the edit modal in read-only mode.
  const handleViewLocation = async (location: any) => {
    await handleEditLocation(location);
    setViewMode(true);
  };

  const handleViewRoom = (room: any) => {
    handleEditRoom(room);
    setViewMode(true);
  };

  const handleViewTrainer = (trainer: any) => {
    handleEditTrainer(trainer);
    setViewMode(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditMode({ isEditing: false, itemId: null, itemType: null });
    setViewMode(false);
  };

  // Delete handlers
  const handleDeleteLocation = (id: number) => {
    showConfirmation(
      'Delete Location',
      'Are you sure you want to delete this location? This action cannot be undone.',
      async () => {
        const result = await removeLocation(id);
        if (result.success) {
          showMessageModal('success', 'Success', result.message || 'Location deleted successfully!');
        } else {
          showMessageModal('error', 'Error', result.message || 'Failed to delete location');
        }
      }
    );
  };

  const handleDeleteRoom = (id: number) => {
    showConfirmation(
      'Delete Room',
      'Are you sure you want to delete this room? This action cannot be undone.',
      async () => {
        const result = await removeRoom(id);
        if (result.success) {
          showMessageModal('success', 'Success', result.message || 'Room deleted successfully!');
        } else {
          showMessageModal('error', 'Error', result.message || 'Failed to delete room');
        }
      }
    );
  };

  const handleDeleteTrainer = (id: number) => {
    showConfirmation(
      'Delete Trainer',
      'Are you sure you want to delete this trainer? This action cannot be undone.',
      async () => {
        const result = await removeTrainer(id);
        if (result.success) {
          showMessageModal('success', 'Success', result.message || 'Trainer deleted successfully!');
        } else {
          showMessageModal('error', 'Error', result.message || 'Failed to delete trainer');
        }
      }
    );
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.address || !formData.countryId || !formData.stateId || !formData.districtId) {
      showMessageModal('error', 'Validation Error', 'Please fill in all required fields (Name, Address, Country, State, District)');
      return;
    }

    const locationData = {
      name: formData.name,
      address: formData.address,
      city: formData.city,
      state: formData.state,
      zipCode: formData.zipCode,
      country: formData.country,
      countryId: formData.countryId,
      stateId: formData.stateId,
      districtId: formData.districtId,
      timezone: formData.timezone,
      capacity: parseInt(formData.capacity) || 0,
      phone: formData.phone,
      email: formData.email,
      facilities: formData.facilities,
    };

    let result;
    if (editMode.isEditing && editMode.itemId) {
      result = await editLocation(editMode.itemId, locationData);
    } else {
      result = await addLocation(locationData);
    }

    if (result.success) {
      showMessageModal('success', 'Success', result.message || `Location ${editMode.isEditing ? 'updated' : 'created'} successfully!`);
      setShowModal(false);
      setEditMode({ isEditing: false, itemId: null, itemType: null });
      setFormData({
        name: '', address: '', city: '', state: '', zipCode: '',
        country: 'India', timezone: '', capacity: '', phone: '', email: '',
        facilities: [] as string[],
        countryId: null,
        stateId: null,
        districtId: null
      });
      setStates([]);
      setDistricts([]);
    } else {
      showMessageModal('error', 'Error', result.message || `Failed to ${editMode.isEditing ? 'update' : 'create'} location`);
    }
  };

  const handleRoomSubmit = async () => {
    if (!roomFormData.name || !roomFormData.location || !roomFormData.capacity) {
      showMessageModal('error', 'Validation Error', 'Please fill in all required fields');
      return;
    }

    // Find the location ID from the selected location name
    const selectedLocation = storeLocations?.find(loc => loc.name === roomFormData.location);
    if (!selectedLocation) {
      showMessageModal('error', 'Validation Error', 'Please select a valid location');
      return;
    }

    const roomData = {
      name: roomFormData.name,
      roomNumber: roomFormData.roomNumber,
      location: roomFormData.location,
      locationId: selectedLocation.id,
      floor: roomFormData.floor,
      type: roomFormData.type,
      capacity: parseInt(roomFormData.capacity) || 0,
      area: roomFormData.area,
      description: roomFormData.description,
      amenities: roomFormData.amenities,
    };

    let result;
    if (editMode.isEditing && editMode.itemId) {
      result = await editRoom(editMode.itemId, roomData);
    } else {
      result = await addRoom(roomData);
    }

    if (result.success) {
      showMessageModal('success', 'Success', result.message || `Room ${editMode.isEditing ? 'updated' : 'created'} successfully!`);
      setShowModal(false);
      setEditMode({ isEditing: false, itemId: null, itemType: null });
      setRoomFormData({
        name: '',
        roomNumber: '',
        floor: '',
        location: '',
        type: 'Conference',
        capacity: '',
        area: '',
        description: '',
        amenities: [] as string[]
      });
    } else {
      showMessageModal('error', 'Error', result.message || `Failed to ${editMode.isEditing ? 'update' : 'create'} room`);
    }
  };

  const handleTrainerSubmit = async () => {
    if (!trainerFormData.firstName || !trainerFormData.lastName || !trainerFormData.email) {
      showMessageModal('error', 'Validation Error', 'Please fill in all required fields');
      return;
    }

    // Create mode requires linking to an existing instructor account.
    if (!editMode.isEditing && !selectedInstructorId) {
      showMessageModal('error', 'Select Instructor', 'Please select an instructor to add as a trainer.');
      return;
    }

    const trainerData = {
      userId: selectedInstructorId, // set from the selected instructor (create mode)
      firstName: trainerFormData.firstName,
      lastName: trainerFormData.lastName,
      email: trainerFormData.email,
      phone: trainerFormData.phone,
      bio: trainerFormData.bio,
      expertise: trainerFormData.expertise,
      certifications: trainerFormData.certifications,
      experience: parseInt(trainerFormData.experience) || 0,
      rate: parseInt(trainerFormData.rate) || 0,
      availability: trainerFormData.availability,
      languages: trainerFormData.languages,
      isExternal: trainerFormData.isExternal,
    };

    let result;
    if (editMode.isEditing && editMode.itemId) {
      result = await editTrainer(editMode.itemId, trainerData);
    } else {
      result = await addTrainer(trainerData);
    }

    if (result.success) {
      showMessageModal('success', 'Success', result.message || `Trainer ${editMode.isEditing ? 'updated' : 'added'} successfully!`);
      setShowModal(false);
      setEditMode({ isEditing: false, itemId: null, itemType: null });
      setTrainerFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        bio: '',
        expertise: [] as string[],
        certifications: [] as string[],
        experience: '',
        rate: '',
        availability: 'Full-Time',
        languages: [] as string[],
        isExternal: false
      });
      setSelectedInstructorId('');
    } else {
      showMessageModal('error', 'Error', result.message || `Failed to ${editMode.isEditing ? 'add' : 'update'} trainer`);
    }
  };

  const matchesText = (haystack: string | undefined | null, term: string) =>
    (haystack ?? '').toLowerCase().includes(term);

  const filteredLocations = locations.filter(loc => {
    // 'all' shows everything; any other value matches the location's status
    // (case-insensitive). Previously non-'active' values fell through to true,
    // so selecting Maintenance/Inactive showed all locations.
    const matchesStatus = filterStatus === 'all'
      ? true
      : (loc.status ?? '').toLowerCase() === filterStatus.toLowerCase();
    const term = searchQuery.trim().toLowerCase();
    const matchesSearch = !term ||
      matchesText(loc.name, term) ||
      matchesText(loc.city, term) ||
      matchesText(loc.state, term) ||
      matchesText(loc.country, term) ||
      matchesText(loc.address, term) ||
      matchesText(loc.email, term) ||
      matchesText(loc.phone, term);
    return matchesStatus && matchesSearch;
  });

  const filteredRooms = rooms.filter(room => {
    // Rooms only have an "active" state in practice — ignore the (hidden)
    // status filter so it doesn't accidentally narrow results to zero.
    const term = searchQuery.trim().toLowerCase();
    const matchesSearch = !term ||
      matchesText(room.name, term) ||
      matchesText(room.location, term) ||
      matchesText(room.type, term) ||
      matchesText((room as any).roomNumber, term);
    return matchesSearch;
  });

  const filteredTrainers = trainers.filter(trainer => {
    const matchesStatus = filterStatus === 'active'
      ? (trainer.status ?? '').toLowerCase() === 'active'
      : true;
    const term = searchQuery.trim().toLowerCase();
    const matchesSearch = !term ||
      matchesText(`${trainer.firstName} ${trainer.lastName}`, term) ||
      matchesText(trainer.email, term) ||
      matchesText(trainer.phone, term) ||
      ((trainer as any).expertise ?? []).some((e: string) => matchesText(e, term));
    return matchesStatus && matchesSearch;
  });

  const getFacilityIcon = (facility: string) => {
    const lowerFacility = facility.toLowerCase();
    if (lowerFacility.includes('parking')) return <FaParking />;
    if (lowerFacility.includes('wifi')) return <FaWifi />;
    if (lowerFacility.includes('cafeteria') || lowerFacility.includes('catering')) return <FaUtensils />;
    if (lowerFacility.includes('av') || lowerFacility.includes('equipment')) return <FaDesktop />;
    return null;
  };

  const getRoomIcon = (type: string) => {
    switch (type.toLowerCase()) {
      case 'conference':
        return <FaUsers />;
      case 'lab':
        return <FaDesktop />;
      case 'classroom':
        return <FaDoorOpen />;
      case 'virtual':
        return <FaDesktop />;
      default:
        return <FaDoorOpen />;
    }
  };

  const getRoomIconColor = (type: string) => {
    switch (type.toLowerCase()) {
      case 'conference':
        return '#4CAF50';
      case 'lab':
        return '#2196F3';
      case 'classroom':
        return '#FF9800';
      case 'virtual':
        return '#9C27B0';
      default:
        return '#2196F3';
    }
  };

  return (
    <div className="trainer-ilt-container">
      <div className="trainer-ilt-header">
        <div className="trainer-ilt-breadcrumb">
          <span>Tools</span>
          <span className="trainer-ilt-breadcrumb-separator">›</span>
          <span>Ilt Resources</span>
        </div>
        <h1 className="trainer-ilt-title">Tools</h1>
        <p className="trainer-ilt-subtitle">Administrative utilities and system tools</p>
      </div>

      <div className="trainer-ilt-section">
        <h2 className="trainer-ilt-section-title">ILT Resource Management</h2>
        <p className="trainer-ilt-section-subtitle">Manage training locations, rooms, and instructors</p>

        <div className="trainer-ilt-stats">
          <div className="trainer-ilt-stat-card">
            <div className="trainer-ilt-stat-icon trainer-ilt-stat-icon-blue">
              <FaBuilding />
            </div>
            <div className="trainer-ilt-stat-content">
              <div className="trainer-ilt-stat-label">Total Locations</div>
              <div className="trainer-ilt-stat-value">{stats.totalLocations}</div>
              <div className="trainer-ilt-stat-change">+2</div>
            </div>
          </div>

          <div className="trainer-ilt-stat-card">
            <div className="trainer-ilt-stat-icon trainer-ilt-stat-icon-green">
              <FaDoorOpen />
            </div>
            <div className="trainer-ilt-stat-content">
              <div className="trainer-ilt-stat-label">Available Rooms</div>
              <div className="trainer-ilt-stat-value">{stats.availableRooms}</div>
              <div className="trainer-ilt-stat-change">+5</div>
            </div>
          </div>

          <div className="trainer-ilt-stat-card">
            <div className="trainer-ilt-stat-icon trainer-ilt-stat-icon-purple">
              <FaUserTie />
            </div>
            <div className="trainer-ilt-stat-content">
              <div className="trainer-ilt-stat-label">Active Trainers</div>
              <div className="trainer-ilt-stat-value">{stats.activeTrainers}</div>
              <div className="trainer-ilt-stat-change">+3</div>
            </div>
          </div>

          <div className="trainer-ilt-stat-card">
            <div className="trainer-ilt-stat-icon trainer-ilt-stat-icon-orange">
              <FaUsers />
            </div>
            <div className="trainer-ilt-stat-content">
              <div className="trainer-ilt-stat-label">Total Capacity</div>
              <div className="trainer-ilt-stat-value">{stats.totalCapacity}</div>
              <div className="trainer-ilt-stat-change">+180</div>
            </div>
          </div>
        </div>

        <div className="trainer-ilt-tabs">
          <button 
            className={`trainer-ilt-tab ${activeTab === 'locations' ? 'trainer-ilt-tab-active' : ''}`}
            onClick={() => setActiveTab('locations')}
          >
            <FaBuilding /> Locations
          </button>
          <button 
            className={`trainer-ilt-tab ${activeTab === 'rooms' ? 'trainer-ilt-tab-active' : ''}`}
            onClick={() => setActiveTab('rooms')}
          >
            <FaDoorOpen /> Rooms
          </button>
          <button 
            className={`trainer-ilt-tab ${activeTab === 'trainers' ? 'trainer-ilt-tab-active' : ''}`}
            onClick={() => setActiveTab('trainers')}
          >
            <FaUserTie /> Trainers
          </button>
        </div>

        <div className="trainer-ilt-controls">
          <div className="trainer-ilt-search-container">
            <FaSearch className="trainer-ilt-search-icon" />
            <input
              type="text"
              placeholder={activeTab === 'rooms' ? 'Search Rooms' : activeTab === 'trainers' ? 'Search Trainers' : 'Search locations'}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="trainer-ilt-search-input"
            />
          </div>
          {activeTab !== 'rooms' && (
            <select
              className="trainer-ilt-filter"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <option value="active">By Status Active</option>
              <option value="all">All Status</option>
              <option value="maintenance">Maintenance</option>
              <option value="inactive">Inactive</option>
            </select>
          )}
          <button
            className="trainer-ilt-btn-primary"
            onClick={() => {
              if (activeTab === 'trainers') fetchAvailableInstructors();
              setShowModal(true);
            }}
          >
            <FaPlus /> {activeTab === 'rooms' ? 'Add Room' : activeTab === 'trainers' ? 'New Instructor' : 'Add Location'}
          </button>
        </div>

        {activeTab === 'locations' && (
          <div className="trainer-ilt-locations-grid">
            {filteredLocations.map(location => (
              <div key={location.id} className="trainer-ilt-location-card">
              <div className="trainer-ilt-location-header">
                <div className="trainer-ilt-location-icon">
                  <FaBuilding />
                </div>
                <div className="trainer-ilt-location-title">
                  <h3 className="trainer-ilt-location-name">{location.name}</h3>
                  <p className="trainer-ilt-location-city">{location.city}</p>
                </div>
                <span className={`trainer-ilt-status trainer-ilt-status-${location.status.toLowerCase()}`}>
                  {location.status}
                </span>
              </div>

              <div className="trainer-ilt-location-details">
                <div className="trainer-ilt-detail-item">
                  <FaMapMarkerAlt /> {location.address}
                </div>
                <div className="trainer-ilt-detail-item">
                  <FaEnvelope /> {location.email}
                </div>
                <div className="trainer-ilt-detail-item">
                  <FaPhone /> {location.phone}
                </div>
              </div>

              <div className="trainer-ilt-location-info">
                <div className="trainer-ilt-info-group">
                  <div className="trainer-ilt-info-item">
                    <div className="trainer-ilt-info-label">Capacity</div>
                    <div className="trainer-ilt-info-value">{location.capacity}</div>
                  </div>
                  <div className="trainer-ilt-info-item">
                    <div className="trainer-ilt-info-label">Rooms</div>
                    <div className="trainer-ilt-info-value">{location.rooms}</div>
                  </div>
                </div>
                <div className="trainer-ilt-info-group">
                  <div className="trainer-ilt-info-item">
                    <div className="trainer-ilt-info-label">Timezone</div>
                    <div className="trainer-ilt-info-value">{location.timezone}</div>
                  </div>
                  <div className="trainer-ilt-info-item">
                    <div className="trainer-ilt-info-label">Manager</div>
                    <div className="trainer-ilt-info-value">{location.manager}</div>
                  </div>
                </div>
              </div>

              <div className="trainer-ilt-facilities">
                <div className="trainer-ilt-facilities-label">Facilities</div>
                <div className="trainer-ilt-facilities-list">
                  {location.facilities.map((facility, idx) => (
                    <span key={idx} className="trainer-ilt-facility-tag">
                      {getFacilityIcon(facility)}
                      {facility}
                    </span>
                  ))}
                </div>
              </div>

              <div className="trainer-ilt-location-actions">
                <button className="trainer-ilt-btn-view" onClick={() => handleViewLocation(location)}>View Details</button>
                <button className="trainer-ilt-icon-btn" onClick={() => handleEditLocation(location)}>
                  <FaEdit />
                </button>
                <button className="trainer-ilt-icon-btn" onClick={() => handleDeleteLocation(location.id)}>
                  <FaTrash />
                </button>
              </div>
            </div>
          ))}
          </div>
        )}

        {activeTab === 'rooms' && (
          <div className="trainer-ilt-locations-grid">
            {filteredRooms.map(room => (
              <div key={room.id} className="trainer-ilt-location-card">
                <div className="trainer-ilt-location-header">
                  <div className="trainer-ilt-location-icon" style={{ backgroundColor: getRoomIconColor(room.type) }}>
                    {getRoomIcon(room.type)}
                  </div>
                  <div className="trainer-ilt-location-title">
                    <h3 className="trainer-ilt-location-name">{room.name}</h3>
                    <p className="trainer-ilt-location-city">Room {room.roomNumber}</p>
                  </div>
                  <span className={`trainer-ilt-status trainer-ilt-status-${room.status.toLowerCase()}`}>
                    {room.status}
                  </span>
                </div>

                <div className="trainer-ilt-location-details">
                  <div className="trainer-ilt-detail-item">
                    <FaBuilding /> {room.location}
                  </div>
                  <div className="trainer-ilt-detail-item">
                    <FaMapMarkerAlt /> {room.floor}
                  </div>
                </div>

                <div className="trainer-ilt-location-info">
                  <div className="trainer-ilt-info-group">
                    <div className="trainer-ilt-info-item">
                      <div className="trainer-ilt-info-label">Type</div>
                      <div className="trainer-ilt-info-value">{room.type}</div>
                    </div>
                    <div className="trainer-ilt-info-item">
                      <div className="trainer-ilt-info-label">Capacity</div>
                      <div className="trainer-ilt-info-value">{room.capacity}</div>
                    </div>
                  </div>
                  <div className="trainer-ilt-info-group">
                    <div className="trainer-ilt-info-item">
                      <div className="trainer-ilt-info-label">Area</div>
                      <div className="trainer-ilt-info-value">{room.area}</div>
                    </div>
                  </div>
                </div>

                <div className="trainer-ilt-facilities">
                  <div className="trainer-ilt-facilities-label">Amenities</div>
                  <div className="trainer-ilt-facilities-list">
                    {room.amenities.map((amenity, idx) => (
                      <span key={idx} className="trainer-ilt-facility-tag">
                        {amenity}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="trainer-ilt-location-actions">
                  <button className="trainer-ilt-btn-view" onClick={() => handleViewRoom(room)}>View Details</button>
                  <button className="trainer-ilt-icon-btn" onClick={() => handleEditRoom(room)}>
                    <FaEdit />
                  </button>
                  <button className="trainer-ilt-icon-btn" onClick={() => handleDeleteRoom(room.id)}>
                    <FaTrash />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {activeTab === 'trainers' && (
          <div className="trainer-ilt-locations-grid">
            {filteredTrainers.map(trainer => (
              <div key={trainer.id} className="trainer-ilt-location-card">
                <div className="trainer-ilt-location-header">
                  <div className="trainer-ilt-location-icon" style={{ backgroundColor: trainer.color, fontSize: '18px', fontWeight: '600' }}>
                    {trainer.initials}
                  </div>
                  <div className="trainer-ilt-location-title">
                    <h3 className="trainer-ilt-location-name">{trainer.firstName} {trainer.lastName}</h3>
                    <p className="trainer-ilt-location-city">{trainer.email}</p>
                  </div>
                  <span className={`trainer-ilt-status trainer-ilt-status-${trainer.status.toLowerCase().replace(' ', '')}`}>
                    {trainer.status}
                  </span>
                </div>

                <div className="trainer-ilt-detail-item" style={{ marginBottom: '12px' }}>
                  <FaStar style={{ color: '#FFA000' }} /> {trainer.rating} · {trainer.sessions} sessions
                </div>

                <p style={{ fontSize: '13px', color: '#666', marginBottom: '16px', lineHeight: '1.5' }}>
                  {trainer.bio}
                </p>

                <div className="trainer-ilt-facilities" style={{ marginBottom: '16px' }}>
                  <div className="trainer-ilt-facilities-label">Expertise</div>
                  <div className="trainer-ilt-facilities-list">
                    {trainer.expertise.map((skill, idx) => (
                      <span key={idx} className="trainer-ilt-facility-tag" style={{ backgroundColor: '#E3F2FD', color: '#1976D2' }}>
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="trainer-ilt-facilities" style={{ marginBottom: '16px' }}>
                  <div className="trainer-ilt-facilities-label">Certifications</div>
                  <div className="trainer-ilt-facilities-list">
                    {trainer.certifications.map((cert, idx) => (
                      <span key={idx} className="trainer-ilt-facility-tag">
                        {cert}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="trainer-ilt-location-info">
                  <div className="trainer-ilt-info-item">
                    <div className="trainer-ilt-info-label">Experience</div>
                    <div className="trainer-ilt-info-value">{trainer.experience} years</div>
                  </div>
                  <div className="trainer-ilt-info-item">
                    <div className="trainer-ilt-info-label">Rate</div>
                    <div className="trainer-ilt-info-value">${trainer.rate}/hr</div>
                  </div>
                  <div className="trainer-ilt-info-item">
                    <div className="trainer-ilt-info-label">Availability</div>
                    <div className="trainer-ilt-info-value">{trainer.availability}</div>
                  </div>
                </div>

                <div className="trainer-ilt-detail-item" style={{ marginTop: '12px' }}>
                  <FaGlobeAmericas /> {trainer.languages.join(', ')}
                </div>

                <div className="trainer-ilt-location-actions">
                  <button className="trainer-ilt-btn-view" onClick={() => handleViewTrainer(trainer)}>View Profile</button>
                  <button className="trainer-ilt-icon-btn" onClick={() => handleEditTrainer(trainer)}>
                    <FaEdit />
                  </button>
                  <button className="trainer-ilt-icon-btn" onClick={() => handleDeleteTrainer(trainer.id)}>
                    <FaTrash />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showModal && (
        <div className="trainer-ilt-modal-overlay" onClick={closeModal}>
          <div className="trainer-ilt-modal" onClick={(e) => e.stopPropagation()}>
            <div className="trainer-ilt-modal-header">
              <h3 className="trainer-ilt-modal-title">
                {viewMode
                  ? activeTab === 'rooms'
                    ? 'Room Details'
                    : activeTab === 'trainers'
                    ? 'Trainer Profile'
                    : 'Location Details'
                  : activeTab === 'rooms'
                  ? (editMode.isEditing ? 'Edit Room' : 'Add New Room')
                  : activeTab === 'trainers'
                  ? (editMode.isEditing ? 'Edit Trainer' : 'Add New Trainer')
                  : (editMode.isEditing ? 'Edit Location' : 'Add New Location')}
              </h3>
              <button
                className="trainer-ilt-modal-close"
                onClick={closeModal}
              >
                <FaTimes />
              </button>
            </div>

            {activeTab === 'locations' && (
              <div className="trainer-ilt-form">
              <div className="trainer-ilt-form-group">
                <label className="trainer-ilt-label">Location Name *</label>
                <input 
                  type="text" 
                  name="name"
                  placeholder="e.g., New York Training Center"
                  value={formData.name}
                  onChange={handleInputChange}
                  className="trainer-ilt-input"
                />
              </div>

              <div className="trainer-ilt-form-group">
                <label className="trainer-ilt-label">Address *</label>
                <input 
                  type="text" 
                  name="address"
                  placeholder="123 Main Street"
                  value={formData.address}
                  onChange={handleInputChange}
                  className="trainer-ilt-input"
                />
              </div>

              <div className="trainer-ilt-form-row">
                <div className="trainer-ilt-form-group">
                  <label className="trainer-ilt-label">Country *</label>
                  <select
                    name="countryId"
                    value={formData.countryId || ''}
                    onChange={handleCountryChange}
                    className="trainer-ilt-select"
                    disabled={loadingCountries}
                  >
                    <option value="">{loadingCountries ? 'Loading...' : 'Select Country'}</option>
                    {countries.map(country => (
                      <option key={country.id} value={country.id}>
                        {country.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="trainer-ilt-form-group">
                  <label className="trainer-ilt-label">State *</label>
                  <select
                    name="stateId"
                    value={formData.stateId || ''}
                    onChange={handleStateChange}
                    className="trainer-ilt-select"
                    disabled={!formData.countryId || loadingStates}
                  >
                    <option value="">
                      {loadingStates ? 'Loading...' : !formData.countryId ? 'Select Country First' : 'Select State'}
                    </option>
                    {states.map(state => (
                      <option key={state.id} value={state.id}>
                        {state.name} {state.type === 'Union Territory' ? '(UT)' : ''}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="trainer-ilt-form-row">
                <div className="trainer-ilt-form-group">
                  <label className="trainer-ilt-label">District *</label>
                  <select
                    name="districtId"
                    value={formData.districtId || ''}
                    onChange={handleDistrictChange}
                    className="trainer-ilt-select"
                    disabled={!formData.stateId || loadingDistricts}
                  >
                    <option value="">
                      {loadingDistricts ? 'Loading...' : !formData.stateId ? 'Select State First' : 'Select District'}
                    </option>
                    {districts.map(district => (
                      <option key={district.id} value={district.id}>
                        {district.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="trainer-ilt-form-group">
                  <label className="trainer-ilt-label">Zip Code</label>
                  <input
                    type="text"
                    name="zipCode"
                    placeholder="10001"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    value={formData.zipCode}
                    onChange={(e) => {
                      const v = e.target.value.replace(/\D/g, '');
                      setFormData(prev => ({ ...prev, zipCode: v }));
                    }}
                    className="trainer-ilt-input"
                  />
                </div>
              </div>

              <div className="trainer-ilt-form-row">
                <div className="trainer-ilt-form-group">
                  <label className="trainer-ilt-label">Timezone</label>
                  <select 
                    name="timezone"
                    value={formData.timezone}
                    onChange={handleInputChange}
                    className="trainer-ilt-select"
                  >
                    <option value="">IST</option>
                    <option value="EST">EST</option>
                    <option value="CST">CST</option>
                    <option value="MST">MST</option>
                    <option value="PST">PST</option>
                  </select>
                </div>
                <div className="trainer-ilt-form-group">
                  <label className="trainer-ilt-label">Total Capacity</label>
                  <input
                    type="number"
                    name="capacity"
                    placeholder="100"
                    min={0}
                    step={1}
                    value={formData.capacity}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v === '' || /^\d+$/.test(v)) {
                        setFormData(prev => ({ ...prev, capacity: v }));
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === '-' || e.key === 'e' || e.key === '+') e.preventDefault();
                    }}
                    className="trainer-ilt-input"
                  />
                </div>
              </div>

              <div className="trainer-ilt-form-row">
                <div className="trainer-ilt-form-group">
                  <label className="trainer-ilt-label">Phone</label>
                  <input
                    type="tel"
                    name="phone"
                    placeholder="+91 9876543210"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    value={formData.phone}
                    onChange={(e) => {
                      const v = e.target.value.replace(/\D/g, '');
                      setFormData(prev => ({ ...prev, phone: v }));
                    }}
                    className="trainer-ilt-input"
                  />
                </div>
                <div className="trainer-ilt-form-group">
                  <label className="trainer-ilt-label">Email</label>
                  <input 
                    type="email" 
                    name="email"
                    placeholder="location@company.com"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="trainer-ilt-input"
                  />
                </div>
              </div>

              <div className="trainer-ilt-form-group">
                <label className="trainer-ilt-label">Facilities</label>
                <div className="trainer-ilt-facilities-grid">
                  {facilityOptions.map(facility => (
                    <label key={facility.id} className="trainer-ilt-checkbox-label">
                      <input 
                        type="checkbox"
                        checked={formData.facilities.includes(facility.label)}
                        onChange={() => handleFacilityToggle(facility.label)}
                        className="trainer-ilt-checkbox"
                      />
                      <span>{facility.icon} {facility.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="trainer-ilt-modal-actions">
                <button
                  className="trainer-ilt-btn-secondary"
                  onClick={closeModal}
                >
                  {viewMode ? 'Close' : 'Cancel'}
                </button>
                {!viewMode && (
                  <button
                    className="trainer-ilt-btn-primary"
                    onClick={handleSubmit}
                  >
                    {editMode.isEditing ? 'Update Location' : 'Create Location'}
                  </button>
                )}
              </div>
              </div>
            )}

            {activeTab === 'rooms' && (
              <div className="trainer-ilt-form">
                <div className="trainer-ilt-form-group">
                  <label className="trainer-ilt-label">Room Name</label>
                  <input
                    type="text"
                    name="name"
                    placeholder="e.g., Executive Conference Room"
                    value={roomFormData.name}
                    onChange={handleRoomInputChange}
                    className="trainer-ilt-input"
                  />
                </div>

                <div className="trainer-ilt-form-row">
                  <div className="trainer-ilt-form-group">
                    <label className="trainer-ilt-label">Room Number</label>
                    <input
                      type="text"
                      name="roomNumber"
                      placeholder="401"
                      value={roomFormData.roomNumber}
                      onChange={handleRoomInputChange}
                      className="trainer-ilt-input"
                    />
                  </div>
                  <div className="trainer-ilt-form-group">
                    <label className="trainer-ilt-label">Floor</label>
                    <input
                      type="text"
                      name="floor"
                      placeholder="4th Floor"
                      value={roomFormData.floor}
                      onChange={handleRoomInputChange}
                      className="trainer-ilt-input"
                    />
                  </div>
                </div>

                <div className="trainer-ilt-form-group">
                  <label className="trainer-ilt-label">Location *</label>
                  <select
                    name="location"
                    value={roomFormData.location}
                    onChange={handleRoomInputChange}
                    className="trainer-ilt-select"
                  >
                    <option value="">Select a location</option>
                    {storeLocations && storeLocations.map((location) => (
                      <option key={location.id} value={location.name}>
                        {location.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="trainer-ilt-form-row">
                  <div className="trainer-ilt-form-group">
                    <label className="trainer-ilt-label">Room Type</label>
                    <select
                      name="type"
                      value={roomFormData.type}
                      onChange={handleRoomInputChange}
                      className="trainer-ilt-select"
                    >
                      <option value="Conference">Conference</option>
                      <option value="Lab">Lab</option>
                      <option value="Classroom">Classroom</option>
                      <option value="Virtual">Virtual</option>
                    </select>
                  </div>
                  <div className="trainer-ilt-form-group">
                    <label className="trainer-ilt-label">Capacity *</label>
                    <input
                      type="number"
                      name="capacity"
                      placeholder="30"
                      min={1}
                      step={1}
                      value={roomFormData.capacity}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v === '' || /^\d+$/.test(v)) {
                          setRoomFormData(prev => ({ ...prev, capacity: v }));
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === '-' || e.key === 'e' || e.key === '+') e.preventDefault();
                      }}
                      className="trainer-ilt-input"
                    />
                  </div>
                </div>

                <div className="trainer-ilt-form-group">
                  <label className="trainer-ilt-label">Area (sq ft)</label>
                  <input
                    type="text"
                    name="area"
                    placeholder="1200 sq ft"
                    value={roomFormData.area}
                    onChange={handleRoomInputChange}
                    className="trainer-ilt-input"
                  />
                </div>

                <div className="trainer-ilt-form-group">
                  <label className="trainer-ilt-label">Description</label>
                  <textarea
                    name="description"
                    placeholder="Describe the room features and setup..."
                    value={roomFormData.description}
                    onChange={handleRoomInputChange}
                    className="trainer-ilt-input"
                    rows={3}
                    style={{ resize: 'vertical' }}
                  />
                </div>

                <div className="trainer-ilt-form-group">
                  <label className="trainer-ilt-label">Amenities</label>
                  <div className="trainer-ilt-facilities-grid">
                    {amenityOptions.map(amenity => (
                      <label key={amenity} className="trainer-ilt-checkbox-label">
                        <input
                          type="checkbox"
                          checked={roomFormData.amenities.includes(amenity)}
                          onChange={() => handleAmenityToggle(amenity)}
                          className="trainer-ilt-checkbox"
                        />
                        <span>{amenity}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="trainer-ilt-modal-actions">
                  <button
                    className="trainer-ilt-btn-secondary"
                    onClick={closeModal}
                  >
                    {viewMode ? 'Close' : 'Cancel'}
                  </button>
                  {!viewMode && (
                    <button
                      className="trainer-ilt-btn-primary"
                      onClick={handleRoomSubmit}
                    >
                      <FaDoorOpen style={{ marginRight: '8px' }} />
                      {editMode.isEditing ? 'Update Room' : 'Create Room'}
                    </button>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'trainers' && (
              <div className="trainer-ilt-form">
                {!editMode.isEditing && (
                  <div className="trainer-ilt-form-row">
                    <div className="trainer-ilt-form-group">
                      <label className="trainer-ilt-label">Select Instructor *</label>
                      <select
                        className="trainer-ilt-input"
                        value={selectedInstructorId}
                        onChange={handleInstructorSelect}
                      >
                        <option value="">-- Select an existing instructor --</option>
                        {(instructors || []).map(inst => (
                          <option key={inst.uuid} value={inst.uuid}>
                            {[inst.firstName, inst.lastName].filter(Boolean).join(' ') || 'Unnamed'} ({inst.email})
                          </option>
                        ))}
                      </select>
                      <small className="trainer-ilt-hint">
                        A trainer is linked to an existing instructor account. Selecting one fills the details below.
                      </small>
                    </div>
                  </div>
                )}
                <div className="trainer-ilt-form-row">
                  <div className="trainer-ilt-form-group">
                    <label className="trainer-ilt-label">First Name *</label>
                    <input
                      type="text"
                      name="firstName"
                      placeholder="John"
                      value={trainerFormData.firstName}
                      onChange={handleTrainerInputChange}
                      className="trainer-ilt-input"
                    />
                  </div>
                  <div className="trainer-ilt-form-group">
                    <label className="trainer-ilt-label">Last Name *</label>
                    <input
                      type="text"
                      name="lastName"
                      placeholder="Smith"
                      value={trainerFormData.lastName}
                      onChange={handleTrainerInputChange}
                      className="trainer-ilt-input"
                    />
                  </div>
                </div>

                <div className="trainer-ilt-form-row">
                  <div className="trainer-ilt-form-group">
                    <label className="trainer-ilt-label">Email *</label>
                    <input
                      type="email"
                      name="email"
                      placeholder="john.smith@company.com"
                      value={trainerFormData.email}
                      onChange={handleTrainerInputChange}
                      className="trainer-ilt-input"
                    />
                  </div>
                  <div className="trainer-ilt-form-group">
                    <label className="trainer-ilt-label">Phone</label>
                    <input
                      type="tel"
                      name="phone"
                      placeholder="+1 (555) 123-4567"
                      value={trainerFormData.phone}
                      onChange={handleTrainerInputChange}
                      className="trainer-ilt-input"
                    />
                  </div>
                </div>

                <div className="trainer-ilt-form-group">
                  <label className="trainer-ilt-label">Bio</label>
                  <textarea
                    name="bio"
                    placeholder="Brief professional biography..."
                    value={trainerFormData.bio}
                    onChange={handleTrainerInputChange}
                    className="trainer-ilt-input"
                    rows={3}
                  />
                </div>

                <div className="trainer-ilt-form-group">
                  <label className="trainer-ilt-label">Expertise Areas</label>
                  <div className="trainer-ilt-facilities-grid">
                    {expertiseOptions.map(expertise => (
                      <label key={expertise} className="trainer-ilt-checkbox-label">
                        <input
                          type="checkbox"
                          checked={trainerFormData.expertise.includes(expertise)}
                          onChange={() => handleExpertiseToggle(expertise)}
                          className="trainer-ilt-checkbox"
                        />
                        <span>{expertise}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="trainer-ilt-form-group">
                  <label className="trainer-ilt-label">Certifications</label>
                  <input
                    type="text"
                    placeholder="Add certification..."
                    className="trainer-ilt-input"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && e.currentTarget.value.trim()) {
                        const value = e.currentTarget.value.trim();
                        setTrainerFormData(prev => ({
                          ...prev,
                          certifications: [...prev.certifications, value]
                        }));
                        e.currentTarget.value = '';
                      }
                    }}
                  />
                  {trainerFormData.certifications.length > 0 && (
                    <div className="trainer-ilt-facilities-list" style={{ marginTop: '8px' }}>
                      {trainerFormData.certifications.map((cert, idx) => (
                        <span key={idx} className="trainer-ilt-facility-tag">
                          {cert}
                          <button
                            type="button"
                            onClick={() => {
                              setTrainerFormData(prev => ({
                                ...prev,
                                certifications: prev.certifications.filter((_, i) => i !== idx)
                              }));
                            }}
                            style={{ marginLeft: '4px', background: 'none', border: 'none', cursor: 'pointer', color: '#E65100' }}
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                <div className="trainer-ilt-form-group">
                  <label className="trainer-ilt-checkbox-label" style={{ padding: '12px', marginBottom: '12px' }}>
                    <input
                      type="checkbox"
                      name="isExternal"
                      checked={trainerFormData.isExternal}
                      onChange={handleTrainerInputChange}
                      className="trainer-ilt-checkbox"
                    />
                    <span>External Trainer?</span>
                  </label>
                </div>

                <div className="trainer-ilt-form-row">
                  <div className="trainer-ilt-form-group">
                    <label className="trainer-ilt-label">Experience (years)</label>
                    <input
                      type="number"
                      name="experience"
                      placeholder="0"
                      value={trainerFormData.experience}
                      onChange={handleTrainerInputChange}
                      className="trainer-ilt-input"
                    />
                  </div>
                  <div className="trainer-ilt-form-group">
                    <label className="trainer-ilt-label">Hourly Rate ($)</label>
                    <input
                      type="number"
                      name="rate"
                      placeholder="0"
                      value={trainerFormData.rate}
                      onChange={handleTrainerInputChange}
                      className="trainer-ilt-input"
                    />
                  </div>
                </div>

                <div className="trainer-ilt-form-group">
                  <label className="trainer-ilt-label">Availability</label>
                  <select
                    name="availability"
                    value={trainerFormData.availability}
                    onChange={handleTrainerInputChange}
                    className="trainer-ilt-select"
                  >
                    <option value="Full-Time">Full Time</option>
                    <option value="Part-Time">Part-Time</option>
                    <option value="Contract">Contract</option>
                  </select>
                </div>

                <div className="trainer-ilt-modal-actions">
                  <button
                    className="trainer-ilt-btn-secondary"
                    onClick={closeModal}
                  >
                    {viewMode ? 'Close' : 'Cancel'}
                  </button>
                  {!viewMode && (
                    <button
                      className="trainer-ilt-btn-primary"
                      onClick={handleTrainerSubmit}
                    >
                      <FaUserTie style={{ marginRight: '8px' }} />
                      {editMode.isEditing ? 'Update Trainer' : 'Add Trainer'}
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <ConfirmationModal
        isOpen={confirmationModal.isOpen}
        title={confirmationModal.title}
        message={confirmationModal.message}
        onConfirm={confirmationModal.onConfirm}
        onCancel={() => setConfirmationModal({ ...confirmationModal, isOpen: false })}
        confirmButtonType="danger"
        confirmText="Delete"
        cancelText="Cancel"
      />

      {showMessage && (
        <div className="trainer-ilt-message-modal-overlay" onClick={() => setShowMessage(false)}>
          <div className="trainer-ilt-message-modal" onClick={(e) => e.stopPropagation()}>
            <div className={`trainer-ilt-message-icon ${messageType === 'success' ? 'trainer-ilt-message-icon-success' : 'trainer-ilt-message-icon-error'}`}>
              {messageType === 'success' ? '✓' : '✕'}
            </div>
            <h3 className="trainer-ilt-message-title">{messageTitle}</h3>
            <p className="trainer-ilt-message-text">{messageText}</p>
            <button className="trainer-ilt-message-btn" onClick={() => setShowMessage(false)}>
              Got it
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ILTManagement;